import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, ArrowLeft, Check, Loader } from 'lucide-react';
const ForgotPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    forgotPassword,
    verifyOTP,
    resetPassword
  } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Multi-step form state
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setIsSubmitting(true);
    try {
      await forgotPassword(email);
      setSuccess('Verification code has been sent to your email');
      setStep(2);
      startResendTimer();
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to process your request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!otp.trim()) {
      setError('Please enter the verification code');
      return;
    }
    setIsSubmitting(true);
    try {
      await verifyOTP(email, otp);
      setSuccess('Code verified successfully');
      setStep(3);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');
    if (!newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      await resetPassword(email, otp, newPassword);
      navigate('/auth/reset-success');
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleResendOTP = async () => {
    if (resendDisabled) return;
    setIsSubmitting(true);
    try {
      await forgotPassword(email);
      setSuccess('Verification code has been resent to your email');
      startResendTimer();
    } catch (error) {
      setError('Failed to resend verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const startResendTimer = () => {
    setResendDisabled(true);
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  return <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 max-w-md mx-auto" data-id="element-42">
      {step === 1 && <>
          <h2 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-43">
            Forgot Password
          </h2>
          <p className="text-navy-700 mb-6" data-id="element-44">
            Enter your email address and we'll send you a verification code to
            reset your password.
          </p>
          {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 flex items-center" data-id="element-45">
              <AlertCircle size={20} className="mr-2" data-id="element-46" />
              <span data-id="element-47">{error}</span>
            </div>}
          <form onSubmit={handleEmailSubmit} data-id="element-48">
            <div className="mb-6" data-id="element-49">
              <label htmlFor="email" className="block text-navy-900 font-medium mb-2" data-id="element-50">
                Email Address
              </label>
              <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" placeholder="Enter your email address" data-id="element-51" />
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-md font-medium transition-colors flex items-center justify-center" data-id="element-52">
              {isSubmitting ? <>
                  <Loader size={20} className="animate-spin mr-2" data-id="element-53" />
                  Sending...
                </> : 'Send Verification Code'}
            </button>
          </form>
        </>}
      {step === 2 && <>
          <div className="flex items-center mb-6" data-id="element-54">
            <button onClick={() => setStep(1)} className="text-navy-700 hover:text-navy-900 mr-4" data-id="element-55">
              <ArrowLeft size={20} data-id="element-56" />
            </button>
            <h2 className="text-2xl font-bold text-navy-900" data-id="element-57">Verify Code</h2>
          </div>
          {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 flex items-center" data-id="element-58">
              <AlertCircle size={20} className="mr-2" data-id="element-59" />
              <span data-id="element-60">{error}</span>
            </div>}
          {success && <div className="bg-green-50 text-green-700 p-4 rounded-md mb-6 flex items-center" data-id="element-61">
              <Check size={20} className="mr-2" data-id="element-62" />
              <span data-id="element-63">{success}</span>
            </div>}
          <p className="text-navy-700 mb-6" data-id="element-64">
            We've sent a verification code to{' '}
            <span className="font-medium" data-id="element-65">{email}</span>
          </p>
          <form onSubmit={handleOtpSubmit} data-id="element-66">
            <div className="mb-6" data-id="element-67">
              <label htmlFor="otp" className="block text-navy-900 font-medium mb-2" data-id="element-68">
                Verification Code
              </label>
              <input type="text" id="otp" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 text-center text-lg tracking-widest" placeholder="123456" maxLength={6} data-id="element-69" />
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-md font-medium transition-colors flex items-center justify-center" data-id="element-70">
              {isSubmitting ? <>
                  <Loader size={20} className="animate-spin mr-2" data-id="element-71" />
                  Verifying...
                </> : 'Verify Code'}
            </button>
          </form>
          <div className="mt-6 text-center" data-id="element-72">
            <p className="text-navy-700" data-id="element-73">
              Didn't receive the code?{' '}
              <button onClick={handleResendOTP} disabled={resendDisabled} className={`text-gold-500 hover:underline font-medium ${resendDisabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-74">
                {resendDisabled ? `Resend in ${countdown}s` : 'Resend Code'}
              </button>
            </p>
          </div>
        </>}
      {step === 3 && <>
          <div className="flex items-center mb-6" data-id="element-75">
            <button onClick={() => setStep(2)} className="text-navy-700 hover:text-navy-900 mr-4" data-id="element-76">
              <ArrowLeft size={20} data-id="element-77" />
            </button>
            <h2 className="text-2xl font-bold text-navy-900" data-id="element-78">Reset Password</h2>
          </div>
          {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 flex items-center" data-id="element-79">
              <AlertCircle size={20} className="mr-2" data-id="element-80" />
              <span data-id="element-81">{error}</span>
            </div>}
          <form onSubmit={handlePasswordSubmit} data-id="element-82">
            <div className="mb-6" data-id="element-83">
              <label htmlFor="newPassword" className="block text-navy-900 font-medium mb-2" data-id="element-84">
                New Password
              </label>
              <input type="password" id="newPassword" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${passwordError && !confirmPassword ? 'border-red-500' : 'border-gray-300'}`} placeholder="Minimum 8 characters" data-id="element-85" />
            </div>
            <div className="mb-6" data-id="element-86">
              <label htmlFor="confirmPassword" className="block text-navy-900 font-medium mb-2" data-id="element-87">
                Confirm New Password
              </label>
              <input type="password" id="confirmPassword" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 ${passwordError && confirmPassword ? 'border-red-500' : 'border-gray-300'}`} placeholder="Re-enter your new password" data-id="element-88" />
              {passwordError && <p className="text-red-500 text-sm mt-1" data-id="element-89">{passwordError}</p>}
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-md font-medium transition-colors flex items-center justify-center" data-id="element-90">
              {isSubmitting ? <>
                  <Loader size={20} className="animate-spin mr-2" data-id="element-91" />
                  Resetting Password...
                </> : 'Reset Password'}
            </button>
          </form>
        </>}
      <div className="mt-6 text-center" data-id="element-92">
        <p className="text-navy-700" data-id="element-93">
          Remember your password?{' '}
          <Link to="/auth/login" className="text-gold-500 hover:underline font-medium" data-id="element-94">
            Back to Login
          </Link>
        </p>
      </div>
    </div>;
};
export default ForgotPasswordForm;